import { useEffect, useMemo, useRef, useState } from "react";
import cs from "classnames";

import { Box, Stack, Typography } from "@braintrust/braintrust-ui-components";
import { Sanitize } from "@js/components/sanitize";
import { useNavigate } from "@js/hooks";
import type {
  ApplicationAnswer,
  ApplicationQuestion,
  EmployerBid,
} from "@js/types/jobs";
import { mapKeys } from "@js/utils";

import style from "./style.module.scss";

const questionHeight = 45;

type BidCardApplicationQAListProps = {
  bid: Pick<EmployerBid, "application_answers" | "application_questions">;
  link: string;
};

export const BidCardApplicationQAList = ({
  bid,
  link,
}: BidCardApplicationQAListProps) => {
  const { answers, questionsToDisplay } = useQuestionsAndAnswers(bid);
  const navigate = useNavigate();

  const [showOverlay, setShowOverlay] = useState(false);
  const contentBox = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const singleQuestion = questionsToDisplay[0];

  useEffect(() => {
    if (contentBox && contentRef) {
      const contentWrapper =
        contentBox.current?.getBoundingClientRect().height || 0;
      const contentHeight =
        contentRef.current?.getBoundingClientRect().height || 0;

      const _showOverlay = contentHeight > contentWrapper;

      setShowOverlay(_showOverlay);
    }
  }, [showOverlay]);

  const shouldOpenOverlayOnClick = showOverlay;

  const handleOverlayClick = () => {
    if (shouldOpenOverlayOnClick) {
      navigate(link);
    }
  };

  if (!singleQuestion) return null;

  const { id, question } = singleQuestion;

  return (
    <Box
      className={cs(style.applicationQuestion, {
        [style.applicationQuestionClickable]: shouldOpenOverlayOnClick,
      })}
      sx={{ mb: 2 }}
    >
      <Typography component="p" variant="label" size="small">
        {question}
      </Typography>
      <Box
        ref={contentBox}
        className={cs(style.answerContent, {
          [style.answerContentOverlayTextShadow]: showOverlay,
        })}
        onClick={handleOverlayClick}
        sx={{ height: questionHeight }}
      >
        {answers[id] ? (
          <Box ref={contentRef}>
            <Sanitize input={answers[id].answer} />
          </Box>
        ) : (
          <span className="color-gray">No Answer.</span>
        )}
      </Box>
    </Box>
  );
};

type ApplicationQaListProps = {
  bid: Pick<
    EmployerBid,
    | "application_answers"
    | "application_questions"
    | "legally_authorized_to_work"
    | "visa_sponsorship_may_be_required"
  >;
};

export const ApplicationQAList = ({ bid }: ApplicationQaListProps) => {
  const { answers, questionsToDisplay } = useQuestionsAndAnswers(bid);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography component="p" variant="paragraph" size="large" sx={{ mb: 2 }}>
        Application questions
      </Typography>
      <Stack sx={{ gap: 2 }}>
        {questionsToDisplay.map(({ id, question }) => {
          return (
            <div key={id} className={cs(style.applicationQuestion)}>
              <Typography component="p" variant="label" size="small">
                {question}
              </Typography>
              <Box className={cs(style.answerContent)}>
                {answers[id] ? (
                  <Box>
                    <Sanitize input={answers[id].answer} />
                  </Box>
                ) : (
                  <span className="color-gray">No Answer.</span>
                )}
              </Box>
            </div>
          );
        })}

        <WorkAuthorizationAnswer
          value={bid.legally_authorized_to_work}
          label="Are you legally authorized to work in the country where the job is
        located?"
        />

        <WorkAuthorizationAnswer
          value={bid.visa_sponsorship_may_be_required}
          label="Will you now or in the future require visa sponsorship to work in the country where the job is located?"
        />
      </Stack>
    </Box>
  );
};

type Answers = {
  [key: number]: ApplicationAnswer;
};

const filterQuestionToDisplay = (
  questions: ApplicationQuestion[],
  answers: Answers,
) => {
  return questions.filter(
    (question) =>
      (question.is_active &&
        !Object.keys(answers).includes(`${question.original_question}`)) ||
      (!question.is_active && Object.keys(answers).includes(`${question.id}`)),
  );
};

const useQuestionsAndAnswers = (
  bid: Pick<EmployerBid, "application_answers" | "application_questions">,
) => {
  const answers = useMemo(
    () => mapKeys(bid.application_answers, "application_question"),
    [bid.application_answers],
  );
  const questionsToDisplay = useMemo(
    () => filterQuestionToDisplay(bid.application_questions, answers),
    [bid.application_questions, answers],
  );

  return { answers, questionsToDisplay };
};

type WorkAuthorizationAnswerProps = {
  label: string;
  value: boolean | undefined;
};

const WorkAuthorizationAnswer = ({
  label,
  value,
}: WorkAuthorizationAnswerProps) => {
  // eslint-disable-next-line eqeqeq
  const hasAnswer = value != null;
  const answer = value ? "Yes" : "No";

  return (
    <Box>
      <Typography component="p" variant="label" size="small">
        {label}
      </Typography>

      <Typography component="p" variant="paragraph" size="medium">
        {hasAnswer && answer}
        {!hasAnswer && <span className="color-gray">No Answer.</span>}
      </Typography>
    </Box>
  );
};
